import { validateRequestedRepoIds } from '@mesa/core/webhook/helpers';
import { generateWebhookSecret } from '@mesa/core/webhook/index';
import {
  createWebhookTarget,
  deleteWebhookTarget,
  regenerateWebhookTargetSecret,
  type UpdateWebhookTargetInput,
  updateWebhookTarget,
} from '@mesa/core/webhook/mutations';
import { getWebhookTarget, listWebhookTargets } from '@mesa/core/webhook/queries';
import type { WebhookEventType } from '@mesa/core/webhook/types';
import { createServerFn } from '@tanstack/react-start';
import { assertScope, baseAuthMiddleware } from '../middleware/base-auth';

export const listWebhookTargetsServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string }) => data)
  .handler(async ({ context: { org, member } }) => {
    assertScope(member.role, 'read');
    const { webhookTargets, reposByWebhookTargetId } = await listWebhookTargets(org.id);
    return webhookTargets.map((target) => ({
      ...target,
      repos: reposByWebhookTargetId.get(target.id) ?? null,
    }));
  });

export const getWebhookTargetServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { org, member } }) => {
    assertScope(member.role, 'read');
    const { webhookTarget, repos } = await getWebhookTarget(org.id, webhookTargetId);
    return { ...webhookTarget, repos };
  });

export const createWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator(
    (data: {
      orgSlug: string;
      url: string;
      events: WebhookEventType[];
      repoIds: string[] | null;
      name?: string | null;
    }) => data
  )
  .handler(async ({ data: { url, events, repoIds, name }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    const validatedRepoIds = await validateRequestedRepoIds(org.id, repoIds ?? undefined);
    const secret = generateWebhookSecret();
    const { webhookTarget } = await createWebhookTarget(org.id, {
      url,
      events,
      repoIds: validatedRepoIds,
      secret,
      name,
    });
    return { ...webhookTarget, repoIds: validatedRepoIds, secret };
  });

export const updateWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; webhookTargetId: string; patch: UpdateWebhookTargetInput }) => data)
  .handler(async ({ data: { webhookTargetId, patch }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    const { repos: existingRepos } = await getWebhookTarget(org.id, webhookTargetId);
    const existingRepoIds = existingRepos?.map((r) => r.id) ?? null;
    const nextRepoIds = patch.repoIds ? await validateRequestedRepoIds(org.id, patch.repoIds) : patch.repoIds;
    const { webhookTarget, repoIds } = await updateWebhookTarget(
      org.id,
      webhookTargetId,
      { ...patch, repoIds: nextRepoIds },
      existingRepoIds
    );
    return { ...webhookTarget, repoIds };
  });

export const regenerateWebhookSecretServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    const secret = generateWebhookSecret();
    const { webhookTarget } = await regenerateWebhookTargetSecret(org.id, webhookTargetId, secret);
    return webhookTarget;
  });

export const deleteWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    await deleteWebhookTarget(org.id, webhookTargetId);
  });
